import { useCallback, useEffect, useRef, useState } from 'react';
import { PageHeader } from '../../components/ui/PageHeader';
import { ReportPageLinks } from '../../components/report/ReportPageLinks';
import { PageToolbar } from '../../components/ui/PageToolbar';
import {
  fetchInventoryOpenPo,
  formatInventoryDate,
  formatInventoryQty,
} from '../../lib/inventoryItems';
import type { InventoryIpgTab, InventoryOpenPoItem, InventoryOpenPoSort } from '../../types/inventory';

/** Load full filtered list in one request (RPC allows up to 10_000). */
const PAGE_SIZE = 10000;

const IPG_TABS: { id: InventoryIpgTab; label: string; ipg: string | null }[] = [
  { id: 'FG-FOCUS', label: 'Focus (XOC/PD/R)', ipg: null },
  { id: 'FG-XOC', label: 'FG-XOC', ipg: 'FG-XOC' },
  { id: 'FG-PD', label: 'FG-PD', ipg: 'FG-PD' },
  { id: 'FG-R', label: 'FG-R', ipg: 'FG-R' },
];

const SORT_OPTIONS: { label: string; value: InventoryOpenPoSort }[] = [
  { label: 'ETA ↑', value: 'eta_asc' },
  { label: 'ETA ↓', value: 'eta_desc' },
  { label: 'Qty ↓', value: 'qty_desc' },
  { label: 'Item A–Z', value: 'item_no_asc' },
];

export function InventoryOpenPoPage() {
  const [tab, setTab] = useState<InventoryIpgTab>('FG-FOCUS');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sort, setSort] = useState<InventoryOpenPoSort>('eta_asc');
  const [rows, setRows] = useState<InventoryOpenPoItem[]>([]);
  const [total, setTotal] = useState(0);
  const [activeSearch, setActiveSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const requestId = useRef(0);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [search]);

  const load = useCallback(async () => {
    const id = ++requestId.current;
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;

    setLoading(true);
    setError(null);

    try {
      const ipg = IPG_TABS.find((t) => t.id === tab)?.ipg ?? null;
      const result = await fetchInventoryOpenPo({
        ipg,
        search: debouncedSearch,
        page: 1,
        pageSize: PAGE_SIZE,
        sort,
        signal: ac.signal,
      });
      if (id !== requestId.current) return;
      setRows(result.items);
      setTotal(result.meta.total);
      setActiveSearch(result.meta.search?.trim() || debouncedSearch);
    } catch (err) {
      if (ac.signal.aborted) return;
      if (id !== requestId.current) return;
      setError(err instanceof Error ? err.message : 'Failed to load open PO.');
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [tab, debouncedSearch, sort]);

  useEffect(() => {
    void load();
    return () => abortRef.current?.abort();
  }, [load]);

  const truncated = total > rows.length;

  return (
    <div className="inventory-page">
      <PageHeader
        title="Open Purchase Orders"
        description="Excel PV / Data Purchase Order — Item × ETA × Outstanding Quantity from PurchaseLines."
        actions={<ReportPageLinks sibling={{ slug: 'item-analysis', label: 'Item Analysis' }} />}
      />

      <div className="inventory-tabs" role="tablist" aria-label="Inventory posting group">
        {IPG_TABS.map((item) => (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={tab === item.id}
            className={`inventory-tabs__btn${tab === item.id ? ' is-active' : ''}`}
            onClick={() => setTab(item.id)}
          >
            {item.label}
          </button>
        ))}
      </div>

      <PageToolbar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search by item, document or description"
        sortValue={SORT_OPTIONS.find((opt) => opt.value === sort)?.label ?? SORT_OPTIONS[0].label}
        sortOptions={SORT_OPTIONS.map((opt) => opt.label)}
        onSortChange={(label) => {
          const next = SORT_OPTIONS.find((opt) => opt.label === label);
          if (next) setSort(next.value);
        }}
      />

      <p className="inventory-page__meta">
        {loading && rows.length === 0
          ? 'Loading…'
          : `${loading ? 'Updating… · ' : ''}${
              truncated
                ? `Showing ${rows.length.toLocaleString('en-SG')} of ${total.toLocaleString('en-SG')} open lines`
                : `${total.toLocaleString('en-SG')} open line${total === 1 ? '' : 's'}${
                    activeSearch ? ` matching “${activeSearch}”` : ''
                  }`
            }`}
      </p>

      {error ? <p className="inventory-page__error">{error}</p> : null}

      <div className="inventory-table-frame">
        <div className="inventory-table-wrap">
          <table className="inventory-table inventory-table--open-po">
            <thead>
              <tr>
                <th className="inventory-col--item">Item No.</th>
                <th>Description</th>
                <th>IPG</th>
                <th>Document No.</th>
                <th>ETA</th>
                <th className="num">Outstanding Qty</th>
                <th>Vendor</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="inventory-table__empty">
                    {loading ? 'Loading…' : 'No open purchase lines.'}
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={`${row.document_no}::${row.line_no}::${row.item_no}`}>
                    <td className="mono inventory-col--item">{row.item_no || '—'}</td>
                    <td>{row.description}</td>
                    <td>{row.ipg || '—'}</td>
                    <td className="mono">{row.document_no}</td>
                    <td>{formatInventoryDate(row.eta)}</td>
                    <td className="num">{formatInventoryQty(row.outstanding_qty)}</td>
                    <td>{row.vendor_no || '—'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
